import apiClient from "@/api/client";

export interface MetricsSummary {
  total_24h: number;
  avg_time_ms: number;
  p95_time_ms: number;
  total_time_ms: number;
  error_4xx: number;
  error_5xx: number;
}

export interface RequestRecord {
  path: string;
  method: string;
  status_code: number | null;
  time_taken: number | null;
  num_sql_queries: number | null;
  start_time: string;
  view_name: string | null;
  error_body: string | null;
  user_name: string | null;
  ip: string | null;
}

export interface CeleryTaskRecord {
  task_id: string;
  task_name: string;
  status: string;
  traceback: string | null;
  result: string | null;
  date_done: string;
}

export interface CelerySummary {
  total_24h: number;
  success_24h: number;
  failure_24h: number;
}

export interface EndpointStat {
  view_name: string;
  method: string;
  example_path: string;
  count: number;
  avg_time_ms: number | null;
  p95_time_ms: number | null;
  total_time_ms: number;
  error_rate: number;
}

export interface RequestsPage {
  requests: RequestRecord[];
  has_more: boolean;
  next_cursor: string | null;
}

export interface CeleryFailuresPage {
  failures: CeleryTaskRecord[];
  has_more: boolean;
  next_cursor: string | null;
  error?: string;
}

export interface DevMetrics {
  silk: {
    summary: MetricsSummary | null;
    requests: RequestRecord[];
    has_more: boolean;
    next_cursor: string | null;
    endpoints: EndpointStat[];
    error?: string;
  };
  celery: {
    summary: CelerySummary | null;
    recent_failures: CeleryTaskRecord[];
    running_workers?: number | null;
    error?: string;
  };
}

export interface RequestFilters {
  method?: string;
  status?: string;
  path?: string;
  user?: string;
  min_ms?: number;
}

export interface InfraPoint {
  ts: string;
  ecs_web_cpu: number | null;
  ecs_web_mem: number | null;
  ecs_web_tasks: number | null;
  ecs_celery_cpu: number | null;
  ecs_celery_mem: number | null;
  ecs_celery_tasks: number | null;
  ecs_qgis_cpu: number | null;
  ecs_qgis_mem: number | null;
  ecs_qgis_tasks: number | null;
  ecs_qgis_cache_cpu: number | null;
  ecs_qgis_cache_mem: number | null;
  ecs_centrifugo_cpu: number | null;
  ecs_centrifugo_mem: number | null;
  ecs_centrifugo_tasks: number | null;
  ecs_collabora_cpu: number | null;
  ecs_collabora_mem: number | null;
  ecs_collabora_tasks: number | null;
  ecs_celery_beat_cpu: number | null;
  ecs_celery_beat_mem: number | null;
  ecs_celery_beat_tasks: number | null;
  redis_engine_cpu: number | null;
  redis_cpu: number | null;
  redis_connections: number | null;
  alb_centrifugo_rt_ms: number | null;
  net_rx_kb: number | null;
  net_tx_kb: number | null;
  rds_cpu: number | null;
  rds_mem_free_mb: number | null;
  rds_storage_free_gb: number | null;
  rds_connections: number | null;
}

// GG (Centrifugo) client-side transport events, minute buckets — reconnect storms
// / subscription churn that never show up on CPU/RAM.
export interface RealtimePoint {
  ts: string;
  connecting: number;
  disconnected: number;
  error: number;
  resubscribe: number;
}

// AWS ALB metrics, one point per (ts, scope). scope = "alb" (whole load
// balancer) or a target group (web/nginx/qgis_cache/centrifugo). Fields not
// applicable to a scope come back null (e.g. hosts on "alb", conns on a TG).
export type LoadBalancerScope =
  | "alb"
  | "web"
  | "nginx"
  | "collabora"
  | "qgis_cache"
  | "centrifugo";

export interface LoadBalancerPoint {
  ts: string;
  scope: LoadBalancerScope;
  request_count: number | null;
  rt_avg_ms: number | null;
  rt_p95_ms: number | null;
  http_2xx: number | null;
  http_4xx: number | null;
  http_5xx: number | null;
  target_conn_errors: number | null;
  healthy_hosts: number | null;
  unhealthy_hosts: number | null;
  elb_5xx: number | null;
  active_conns: number | null;
  new_conns: number | null;
  rejected_conns: number | null;
  processed_kb: number | null;
}

// Async Celery telemetry (CeleryTaskRun): duration, queue wait, per type/user.
export interface CeleryTypeStat {
  task_name: string;
  n: number;
  failures: number;
  avg_ms: number | null;
  p95_ms: number | null;
  avg_wait_ms: number | null;
  p95_wait_ms: number | null;
}
export interface CeleryUserStat {
  user_id: string | null;
  user: string;
  n: number;
  failures: number;
  avg_ms: number | null;
  p95_ms: number | null;
}
export interface CeleryTimingPoint {
  ts: string;
  n: number;
  p95_ms: number | null;
  p95_wait_ms: number | null;
}
export interface CeleryAnalytics {
  by_type: CeleryTypeStat[];
  by_user: CeleryUserStat[];
  timeseries: CeleryTimingPoint[];
}

export interface TimeseriesPoint {
  ts: string;
  total: number;
  ok_2xx: number;
  err_4xx: number;
  err_5xx: number;
  avg_ms: number | null;
  p95_ms: number | null;
  success_rate: number | null;
  drift_ms: number | null;
  shakehand_ms: number | null;
  qgis_shakehand_ms: number | null;
}

export type TestRunStatus = "running" | "passed" | "failed" | "error";

export interface TestRunResult {
  id: string;
  started_at: string;
  finished_at: string | null;
  status: TestRunStatus;
  triggered_by: string;
  total: number;
  passed: number;
  failed: number;
  errored: number;
  skipped: number;
  duration_seconds: number | null;
  failure_summary: string;
  coverage_s3_prefix: string;
}

export interface CoverageReport {
  html: string;
  run_id: string;
  started_at: string;
  status: TestRunStatus;
  coverage_s3_prefix: string;
}

export interface TestRunDetail extends TestRunResult {
  output: string;
}

// ── QGIS perf benchmark ─────────────────────────────────────────────────────

export type PerfRunStatus = "running" | "passed" | "failed" | "error";

export interface PerfScenarioSummary {
  scenario?: string;
  target?: string;
  vus?: number;
  iterations?: number;
  p50_ms?: number | null;
  p90_ms?: number | null;
  p95_ms?: number | null;
  p99_ms?: number | null;
  avg_ms?: number | null;
  throughput_rps?: number | null;
  error_rate?: number | null;
  bytes_per_tile_avg?: number | null;
  cache_hit_ratio?: number | null;
  duration_s?: number | null;
  error?: string;
}

export interface PerfRunResult {
  id: string;
  started_at: string;
  finished_at: string | null;
  status: PerfRunStatus;
  triggered_by: string;
  duration_seconds: number | null;
  regression_flags: string[];
  scenarios_summary: Record<string, PerfScenarioSummary>;
}

export interface PerfRunDetail extends PerfRunResult {
  cw_snapshot: Record<string, number | string | null>;
  raw_s3_prefix: string;
  failure_summary: string;
  celery_task_id: string;
  samples: Record<string, string>; // "<scenario>__<target>" -> presigned URL
}

// Karta „Collabora" (edytor pism): sonda discovery, liczniki WOPI z 24 h i — na
// produkcji — usługa ECS, zdrowie celu ALB, powód zatrzymania zadania, ogon logu.
export interface CollaboraAwsError { error: string }
export interface CollaboraStatus {
  configured: boolean;
  public_url: string;
  internal_url: string;
  wopi_host_url: string;
  probe: { ok: boolean; status: number | null; latency_ms: number | null; url: string; error: string; checked_at: string };
  extensions: { edit: number; view: number };
  wopi: {
    sessions: number; saves: number; discarded: number; failures: number;
    last_failure: { at: string; file_id: string; message: string; user_id: string } | null;
  };
  aws: {
    service: {
      status?: string; running?: number; pending?: number; desired?: number; task_definition?: string;
      events?: { at: string; message: string }[]; error?: string;
    } | null;
    target_health: { target: string; state: string; reason?: string; description?: string }[] | CollaboraAwsError | null;
    last_stopped_task: {
      stopped_at: string | null; stopped_reason: string; stop_code?: string; exit_code?: number | null; container_reason: string;
    } | null;
    log_tail: { at: string; message: string }[] | CollaboraAwsError | null;
  } | null;
}

/** One CloudWatch line from /ecs/elg5/web. */
export interface WebLogRow {
  at: string;
  stream: string;
  message: string;
  /** Error lines only: the lines logged right after it (the traceback). */
  context?: string[];
}
export type WebLogSection = { total: number; truncated: boolean; rows: WebLogRow[] } | { error: string };
/** `null` outside production (INFRA_METRICS_ENABLED off). */
export type WebErrorLog = {
  hours: number;
  responses_5xx: WebLogSection | null;
  errors: WebLogSection | null;
} | null;

export const devMetricsApi = {
  getWebErrors: (hours: number) =>
    apiClient
      .get<{ data: WebErrorLog }>("/dev-issues/metrics/web-errors/", { params: { hours } })
      .then((r) => r.data.data),

  getCollaboraStatus: () =>
    apiClient
      .get<{ data: CollaboraStatus }>("/dev-issues/metrics/collabora/")
      .then((r) => r.data.data),

  get: () =>
    apiClient
      .get<{ data: DevMetrics }>("/dev-issues/metrics/")
      .then((res) => res.data.data),

  getTimeseries: (hours: number) =>
    apiClient
      .get<{ data: TimeseriesPoint[] }>("/dev-issues/metrics/timeseries/", { params: { hours } })
      .then((r) => r.data.data),

  getInfraTimeseries: (hours: number) =>
    apiClient
      .get<{ data: InfraPoint[] }>("/dev-issues/metrics/infra/", { params: { hours } })
      .then((r) => r.data.data),

  getRealtimeTimeseries: (hours: number) =>
    apiClient
      .get<{ data: RealtimePoint[] }>("/dev-issues/metrics/realtime/", { params: { hours } })
      .then((r) => r.data.data),

  getLoadBalancerTimeseries: (hours: number) =>
    apiClient
      .get<{ data: LoadBalancerPoint[]; meta: { scopes: LoadBalancerScope[] } }>(
        "/dev-issues/metrics/loadbalancers/",
        { params: { hours } },
      )
      .then((r) => ({ points: r.data.data, scopes: r.data.meta.scopes })),

  getCeleryAnalytics: (hours: number) =>
    apiClient
      .get<{ data: CeleryAnalytics }>("/dev-issues/metrics/celery-analytics/", { params: { hours } })
      .then((r) => r.data.data),

  getUsers: () =>
    apiClient
      .get<{ data: string[] }>("/dev-issues/metrics/users/")
      .then((res) => res.data.data),

  getTestRuns: () =>
    apiClient
      .get<{ data: TestRunResult[] }>("/dev-issues/metrics/test-runs/")
      .then((r) => r.data.data),

  getTestRunDetail: (id: string) =>
    apiClient
      .get<{ data: TestRunDetail }>(`/dev-issues/metrics/test-runs/${id}/`)
      .then((r) => r.data.data),

  triggerTestRun: () =>
    apiClient
      .post<{ data: { task_id: string } }>("/dev-issues/metrics/test-runs/")
      .then((r) => r.data.data),

  abortTestRun: (id: string) =>
    apiClient
      .post<{ data: { revoked: boolean } }>(`/dev-issues/metrics/test-runs/${id}/abort/`)
      .then((r) => r.data.data),

  getPerfRuns: () =>
    apiClient
      .get<{ data: PerfRunResult[] }>("/dev-issues/metrics/perf-runs/")
      .then((r) => r.data.data),

  getPerfRunDetail: (id: string) =>
    apiClient
      .get<{ data: PerfRunDetail }>(`/dev-issues/metrics/perf-runs/${id}/`)
      .then((r) => r.data.data),

  triggerPerfRun: () =>
    apiClient
      .post<{ data: { task_id: string } }>("/dev-issues/metrics/perf-runs/")
      .then((r) => r.data.data),

  abortPerfRun: (id: string) =>
    apiClient
      .post<{ data: { revoked: boolean } }>(`/dev-issues/metrics/perf-runs/${id}/abort/`)
      .then((r) => r.data.data),

  resetSilk: () =>
    apiClient
      .post<{ data: { deleted_requests: number; deleted_meta: number } }>("/dev-issues/metrics/silk-reset/")
      .then((r) => r.data.data),

  getCoverageReport: (runId?: string) =>
    apiClient
      .get<{ data: CoverageReport }>(
        runId
          ? `/dev-issues/metrics/coverage/${runId}/`
          : "/dev-issues/metrics/coverage/",
      )
      .then((r) => r.data.data),

  getCeleryFailures: (before?: string) =>
    apiClient
      .get<{ data: CeleryFailuresPage }>("/dev-issues/metrics/celery-failures/", {
        params: before ? { before } : undefined,
      })
      .then((res) => res.data.data),

  getRequests: (before?: string, filters?: RequestFilters) => {
    const params: Record<string, string> = {};
    if (before) params.before = before;
    if (filters?.method) params.method = filters.method;
    if (filters?.status) params.status = filters.status;
    if (filters?.path) params.path = filters.path;
    if (filters?.user) params.user = filters.user;
    if (filters?.min_ms) params.min_ms = String(filters.min_ms);
    return apiClient
      .get<{ data: RequestsPage }>("/dev-issues/metrics/requests/", { params })
      .then((res) => res.data.data);
  },
};
